import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';

const initialState = {
  faqs: [],
  loading: false,
  error: null,
};

// Async Thunk: Fetch all FAQs
export const fetchFaqs = createAsyncThunk(
  'faq/fetchFaqs',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/faqs');
      if (res.data.success) {
        return res.data.faqs || res.data.data || [];
      }
      return rejectWithValue('Failed to fetch FAQs');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Create an FAQ (Admin Only)
export const createFaq = createAsyncThunk(
  'faq/createFaq',
  async (values, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/faqs', values);
      if (res.data.success) {
        return res.data.faq || res.data.data;
      }
      return rejectWithValue('Failed to create FAQ');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Update an FAQ (Admin Only)
export const updateFaq = createAsyncThunk(
  'faq/updateFaq',
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/faqs/${id}`, values);
      if (res.data.success) {
        return res.data.faq || res.data.data;
      }
      return rejectWithValue('Failed to update FAQ');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Delete an FAQ (Admin Only)
export const deleteFaq = createAsyncThunk(
  'faq/deleteFaq',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/faqs/${id}`);
      if (res.data.success) {
        return id;
      }
      return rejectWithValue('Failed to delete FAQ');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const faqSlice = createSlice({
  name: 'faq',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch FAQs
      .addCase(fetchFaqs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFaqs.fulfilled, (state, action) => {
        state.faqs = action.payload;
        state.loading = false;
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create FAQ
      .addCase(createFaq.pending, (state) => {
        state.loading = true;
      })
      .addCase(createFaq.fulfilled, (state, action) => {
        state.loading = false;
        state.faqs.unshift(action.payload);
      })
      .addCase(createFaq.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update FAQ
      .addCase(updateFaq.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateFaq.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.faqs.findIndex(f => f._id === action.payload._id);
        if (index !== -1) {
          state.faqs[index] = action.payload;
        }
      })
      .addCase(updateFaq.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete FAQ
      .addCase(deleteFaq.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteFaq.fulfilled, (state, action) => {
        state.loading = false;
        state.faqs = state.faqs.filter(f => f._id !== action.payload);
      })
      .addCase(deleteFaq.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default faqSlice.reducer;
